import React, { forwardRef, useId } from 'react';
import { Icon, type IconName } from './icons';
export const cx = (...items: (string | false | undefined | null)[]) => items.filter(Boolean).join(' ');
export type Tone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info' | 'violet';
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
    size?: 'sm' | 'md' | 'lg';
    loading?: boolean;
    icon?: IconName;
    trailingIcon?: IconName;
}
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({ variant = 'secondary', size = 'md', loading = false, icon, trailingIcon, disabled, className, children, type = 'button', ...props }, ref) {
    return <button {...props} ref={ref} type={type} disabled={disabled || loading} aria-busy={loading || undefined} className={cx('bs-button', `bs-button--${variant}`, `bs-button--${size}`, className)}>{loading ? <Spinner size={16}/> : icon && <Icon name={icon} size={16}/>}{children ? <span>{children}</span> : null}{!loading && trailingIcon ? <Icon name={trailingIcon} size={16}/> : null}</button>;
});
export interface IconButtonProps extends Omit<ButtonProps, 'icon' | 'children'> {
    icon: IconName;
    label: string;
}
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton({ icon, label, className, ...props }, ref) {
    return <Button {...props} ref={ref} icon={icon} className={cx('bs-icon-button', className)} aria-label={label} title={label} />;
});
export function Spinner({ size = 18 }: {
    size?: number;
}) { return <span className="bs-spinner" style={{ width: size, height: size }} aria-hidden="true"/>; }
export function Badge({ tone = 'neutral', icon, children, className }: {
    tone?: Tone;
    icon?: IconName;
    children: React.ReactNode;
    className?: string;
}) {
    return <span className={cx('bs-badge', `bs-tone-${tone}`, className)}>{icon && <Icon name={icon} size={12}/>} {children}</span>;
}
export function Tag({ children, onRemove }: {
    children: React.ReactNode;
    onRemove?: () => void;
}) {
    return <span className="bs-tag">{children}{onRemove && <button type="button" onClick={onRemove} aria-label={`Remove ${typeof children === 'string' ? children : 'tag'}`}><Icon name="close" size={12}/></button>}</span>;
}
export function Avatar({ name, src, size = 'md' }: {
    name: string;
    src?: string;
    size?: 'sm' | 'md' | 'lg';
}) {
    return <span className={cx('bs-avatar', `bs-avatar--${size}`)} title={name} aria-label={name}>{src ? <img src={src} alt=""/> : name.split(' ').map(x => x[0]).slice(0, 2).join('').toUpperCase()}</span>;
}
export function AvatarGroup({ people }: {
    people: {
        name: string;
        src?: string;
    }[];
}) { return <span className="bs-avatar-group">{people.slice(0, 4).map(p => <Avatar key={p.name} {...p} size="sm"/>)}{people.length > 4 && <span className="bs-avatar bs-avatar--sm">+{people.length - 4}</span>}</span>; }
export function Tooltip({ label, children }: {
    label: string;
    children: React.ReactElement<{ 'aria-describedby'?: string }>;
}) {
    const id = useId();
    return <span className="bs-tooltip-wrap">{React.cloneElement(children, { 'aria-describedby': id })}<span role="tooltip" id={id} className="bs-tooltip">{label}</span></span>;
}
export function Divider() { return <hr className="bs-divider"/>; }
export function Card({ children, className, ...props }: React.HTMLAttributes<HTMLDivElement>) { return <div {...props} className={cx('bs-card', className)}>{children}</div>; }
export function StatCard({ label, value, detail, icon, tone = 'neutral' }: {
    label: string;
    value: React.ReactNode;
    detail?: string;
    icon: IconName;
    tone?: Tone;
}) {
    return <Card className="bs-stat"><span className={cx('bs-stat-icon', `bs-tone-${tone}`)}><Icon name={icon}/></span><div><span className="bs-label">{label}</span><strong>{value}</strong>{detail && <small className="bs-muted">{detail}</small>}</div></Card>;
}
export function ProgressBar({ value, label, tone = 'accent' }: {
    value?: number;
    label: string;
    tone?: Tone;
}) {
    const progress = value == null ? undefined : Math.min(100, Math.max(0, value));
    return <div className="bs-progress-group"><div className="bs-between bs-small"><span>{label}</span><span className="bs-mono bs-muted">{progress == null ? 'Working…' : `${Math.round(progress)}%`}</span></div><div className={cx('bs-progress', `bs-tone-${tone}`, progress == null && 'bs-indeterminate')} role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}><span style={progress == null ? undefined : { width: `${progress}%` }}/></div></div>;
}
export function Alert({ tone = 'info', title, children, action }: {
    tone?: Tone;
    title: string;
    children?: React.ReactNode;
    action?: React.ReactNode;
}) {
    return <div className={cx('bs-alert', `bs-tone-${tone}`)} role={tone === 'danger' ? 'alert' : 'status'}><Icon name={tone === 'success' ? 'check' : tone === 'danger' || tone === 'warning' ? 'alert' : 'info'}/><div><strong>{title}</strong>{children && <div className="bs-alert-description">{children}</div>}</div>{action}</div>;
}
export function Skeleton({ width = '100%', height = 16, className }: {
    width?: number | string;
    height?: number | string;
    className?: string;
}) { return <span className={cx('bs-skeleton', className)} aria-hidden="true" style={{ width, height }}/>; }
export function EmptyState({ icon = 'image', title, description, action }: {
    icon?: IconName;
    title: string;
    description: string;
    action?: React.ReactNode;
}) {
    return <div className="bs-empty"><span className="bs-empty-icon"><Icon name={icon} size={25}/></span><h3>{title}</h3><p>{description}</p>{action}</div>;
}
export function StatusDot({ label, tone = 'success' }: {
    label: string;
    tone?: Tone;
}) { return <span className={cx('bs-status-dot', `bs-tone-${tone}`)}><i />{label}</span>; }
